import type { Comparison } from "../../lib/comparisons";
import { partial, unstated, yes } from "./cells";

export const amazon: Comparison = {
  name: "Amazon",
  site: "amazon.fr",
  linkLabel: "Liste de naissance Amazon ou Listou",
  checkedOn: "2026-09-29",
  seoTitle: "Liste de naissance Amazon ou Listou ? Comparatif 2026 · Listou",
  seoDescription:
    "Alternative à la liste de naissance Amazon : Listou est gratuit, accepte n'importe quelle boutique, se réserve sans compte et garde la surprise jusqu'au lendemain de l'événement. Le comparatif point par point.",
  h1: "Liste de naissance Amazon ou Listou : que choisir ?",
  summary:
    "La liste de naissance Amazon se remplit avec des articles vendus sur Amazon, y compris par des vendeurs tiers, et donne droit à une réduction de 15 % (plafonnée à 300 €) au titulaire. Listou est une liste gratuite où l'on ajoute un article de n'importe quelle boutique en collant son lien, où les proches réservent sans compte et peuvent acheter où ils veulent, et où les parents ne voient pas ce qui est réservé avant le lendemain de l'événement. Listou se personnalise aussi entièrement : modèle de page, couleurs, police, stickers.",
  rows: [
    {
      feature: "Boutiques",
      listou: yes("N'importe laquelle : on colle le lien, photo et prix sont récupérés"),
      them: partial("Articles trouvés par recherche sur Amazon, vendeurs tiers inclus"),
    },
    {
      feature: "Où les proches peuvent acheter",
      listou: yes("Où ils veulent, en ligne ou en magasin"),
      them: partial("Ajout au panier depuis la liste pour marquer l'article acheté"),
    },
    {
      feature: "Personnaliser le design de la liste",
      listou: yes("6 modèles de page, 13 palettes, 4 polices, 162 stickers, fonds à motifs, photo"),
      them: unstated("Aucune option de design décrite dans l'aide de la liste de naissance"),
    },
    {
      feature: "Les parents ne voient pas ce qui est réservé",
      listou: yes("Oui, par défaut, jusqu'au lendemain de l'événement"),
      them: partial("Par défaut, secret « pendant plusieurs semaines », si connecté"),
    },
    {
      feature: "Adresse de livraison",
      listou: yes("Montrée à l'invité seulement après sa réservation"),
      them: partial(
        "Nom, ville et pays affichés ; adresse complète selon le réglage vendeurs tiers",
      ),
    },
    {
      feature: "Gérer la liste à deux",
      listou: yes("Co-propriétaire, et invités qui peuvent modifier"),
      them: yes("Cotitulaire, et invités en « Lecture et modification »"),
    },
    {
      feature: "Partager la liste",
      listou: yes("Lien, QR code, visuels Instagram, lien régénérable"),
      them: partial("Lien et boutons de réseaux sociaux"),
    },
    {
      feature: "Visibilité de la liste",
      listou: yes("Sur lien uniquement, hors moteurs de recherche"),
      them: partial("Publique, partagée ou privée ; les listes publiques se recherchent"),
    },
  ],
  body: `
## Les boutiques : tout Amazon, ou n'importe quelle boutique

Sur **Amazon**, on crée sa liste de naissance puis on effectue une recherche sur Amazon pour y ajouter des articles. La réduction de la liste s'applique aux articles éligibles, y compris ceux pour bébés vendus par des vendeurs tiers.

Sur **Listou**, on colle le lien d'un produit de n'importe quelle boutique : le nom, la photo et le prix sont récupérés. On peut aussi écrire une envie à la main, ou chercher parmi les produits que d'autres parents ont déjà ajoutés. Pour les idées, un assistant propose des articles selon l'occasion.

Si votre poussette vient d'une boutique spécialisée, le lit d'un créateur et les bodies d'une grande enseigne, tout tient sur une seule liste avec Listou.

## Où vos proches achètent

Sur Amazon, l'aide indique que les articles achetés en les ajoutant au panier depuis la liste sont marqués comme achetés. Un article commandé en dehors de la liste, par exemple depuis sa page produit, n'est pas marqué comme acheté sur la liste.

Sur Listou, le bouton « Voir sur… » mène à la boutique que vous avez choisie, mais vos proches peuvent acheter ailleurs, en ligne ou en magasin. Ils cliquent sur « Je l'offre » : l'article apparaît aussitôt comme pris pour les autres invités, quel que soit l'endroit où il est finalement acheté. Listou ne vend rien et ne prend aucun paiement.

## La réduction de 15 % d'Amazon

C'est un vrai avantage d'Amazon. Les titulaires d'une liste de naissance bénéficient d'une réduction de 15 %, valable sur des commandes d'une valeur totale maximale de 2 000 €, soit une réduction maximale de 300 €. Seuls les articles repris dans la page de réduction de la liste sont éligibles, il faut les ajouter au panier depuis cette page, et seul le titulaire principal de la liste peut en bénéficier.

Listou n'a pas d'équivalent : c'est un service gratuit, pas une boutique.

## Une liste à votre image

Sur **Listou**, tout se règle dans l'onglet Apparence, avec l'aperçu en direct :

- **6 modèles de page** : la mise en page elle-même change, pas seulement les couleurs.
- **13 palettes**, plus une couleur d'accent au choix.
- **4 polices** pour les titres.
- **162 stickers** dessinés, en 18 thèmes, pour décorer l'en-tête ou tapisser le fond en motifs.
- **Une photo de couverture** et un emoji pour la liste.

Les pages d'aide Amazon consacrées à la liste de naissance (création, modification des articles, partage, confidentialité) ne décrivent pas d'option de personnalisation du design de la liste.

## La surprise

Sur **Amazon**, le paramètre « Ne pas me gâcher la surprise » est activé par défaut. Les cadeaux achetés restent secrets pendant plusieurs semaines : l'article continue de s'afficher comme « Non acheté » dans la liste, et Amazon avertit si vous essayez d'acheter un article qu'on vous a déjà offert. L'aide précise que le filtre « Articles achetés » et les avertissements s'affichent uniquement lorsque vous êtes connecté à votre compte : si vous ne vous identifiez pas, la liste peut révéler une surprise.

Sur **Listou**, le mode surprise est activé par défaut. Tant qu'il est actif, ni vous ni votre co-parent ne voyez ce qui est réservé, ni par qui : vous recevez seulement « un cadeau vient d'être réservé ». La révélation a lieu le lendemain de la date de l'événement, ou quand vous le décidez avec « Révéler maintenant ». Si vous ouvrez le lien de votre propre liste, Listou vous montre l'aperçu que voient vos proches, sans les réservations.

Pour une liste d'enfant que vous gérez vous-même, le mode surprise se coupe en un clic : vous voyez ce qui est pris et évitez un doublon.

## Vos proches : compte et réservation

Sur Listou, vos proches ouvrent le lien et cliquent sur « Je l'offre » : ils n'ont aucun compte à créer, et le prénom comme l'email sont facultatifs. Vous, en tant que créateur, vous vous connectez avec un lien envoyé par email, ou avec Google.

Sur Amazon, l'aide de la liste de naissance décrit l'achat par ajout au panier puis « Passer la commande », en indiquant que les invités choisissent l'adresse par défaut ou en saisissent une autre. Elle ne précise pas si un compte est requis.

Pour un article voulu en plusieurs exemplaires (trois bodies, deux assiettes), Listou laisse chacun en réserver une partie, et la liste indique combien il en reste.

## L'adresse de livraison

Sur Amazon, l'adresse de la liste de naissance est confidentielle : la liste et le passage de commande affichent seulement le nom, la ville et le pays du titulaire. Pour les articles vendus par un vendeur tiers, l'adresse par défaut n'est disponible que si le titulaire a activé les expéditions par des tiers, ce qui envoie l'adresse au vendeur, qui peut la communiquer à l'acheteur. Sans cela, l'aide précise que les invités doivent connaître et saisir l'adresse.

Sur Listou, l'adresse n'est jamais affichée sur la liste. Un proche la voit seulement après avoir réservé, à l'écran et dans son mail de confirmation. Réserver ne demandant pas de compte, toute personne qui a le lien peut la voir : ne l'ajoutez que si vous partagez la liste avec vos proches.

## Rester anonyme

Sur Listou, cocher « Rester anonyme » en réservant cache le nom à la personne qui reçoit, même après la révélation : elle voit « un proche ». Les autres invités ne voient jamais le prénom, seulement que le cadeau est pris.

## Préparer la liste à deux

Sur **Amazon**, le formulaire de création comporte un champ pour le cotitulaire : vos proches peuvent retrouver la liste en cherchant son nom. Le partage d'une liste offre deux niveaux : « Lecture seule », et « Lecture et modification », où les personnes invitées ajoutent ou suppriment des articles, modifient les quantités et les priorités, et publient des commentaires. Seuls les propriétaires gèrent les paramètres de confidentialité, la suppression, le partage, l'adresse d'expédition et le nom du destinataire.

Sur **Listou**, vous invitez le co-parent comme **co-propriétaire** : mêmes droits que vous, et la surprise reste gardée pour vous deux. Vous pouvez aussi inviter la marraine ou une amie à « modifier » : elle aide à remplir la liste sans toucher au partage ni à la surprise.

## Partager la liste

Sur **Amazon**, l'option Partager donne un lien vers la liste et des boutons pour la partager sur des réseaux sociaux. Une liste privée ne peut pas être partagée. Trois réglages de visibilité existent : publique, partagée ou privée, et les listes publiques peuvent être recherchées par le nom, la ville ou le pays du titulaire (les listes privées ou partagées n'apparaissent pas).

Sur **Listou**, l'onglet Partage donne le lien, un **QR code** à imprimer sur le faire-part et des **visuels prêts pour Instagram**. Seules les personnes qui ont le lien voient la liste : elle n'apparaît pas dans les moteurs de recherche. Le lien a été trop partagé ? Vous en générez un nouveau, et l'ancien cesse de fonctionner.

## Les participations en argent

Listou ne fait transiter aucun argent. Vous pouvez ajouter un lien vers votre propre cagnotte (Leetchi, Lydia…) : un bloc « Participer librement » apparaît alors en bas de la liste.

## Noël et les fêtes à plusieurs

Listou couvre la naissance, mais aussi Noël, l'anniversaire et le mariage. Un **groupe** réunit plusieurs listes derrière un seul lien, « Noël chez les Martin » : chacun choisit son prénom, remplit sa liste et réserve sur celles des autres. Un tirage au sort Secret Santa peut s'y ajouter, avec des exclusions.

## Passer d'Amazon à Listou

Il n'y a pas d'import automatique. La migration prend quelques minutes :

1. Créez votre liste sur Listou, avec la date de l'événement.
2. Pour chaque article, copiez le lien du produit depuis votre liste Amazon et collez-le dans Listou. Le nom, la photo et le prix sont récupérés.
3. Ajoutez les articles d'autres boutiques que vous aviez repérés : ils tiennent sur la même liste.
4. Invitez votre co-parent dans les réglages.
5. Envoyez le nouveau lien à vos proches, ou imprimez son QR code sur le faire-part. Si l'ancienne liste Amazon a déjà été partagée, pensez à la passer en privée.

Si des cadeaux ont déjà été achetés sur Amazon, retirez-les de la nouvelle liste pour éviter les doublons.
`,
  chooseThem: [
    "Vous voulez profiter de la réduction de 15 % (jusqu'à 300 €) réservée aux titulaires d'une liste de naissance Amazon.",
    "Vous préférez tout faire livrer par un seul marchand, avec un panier et un paiement uniques.",
  ],
  chooseListou: [
    "Vous voulez ajouter des articles de n'importe quelle boutique sur une même liste.",
    "Vos proches doivent pouvoir réserver sans créer de compte et acheter où ils veulent.",
    "Vous voulez découvrir les cadeaux à la naissance : les réservations restent cachées jusqu'au lendemain de l'événement.",
    "Vous voulez une liste qui vous ressemble : modèle, couleurs, police et stickers.",
    "Vous organisez aussi Noël en famille : listes en groupe et Secret Santa au même endroit.",
  ],
  faq: [
    [
      "Quelle est l'alternative à la liste de naissance Amazon ?",
      "Listou, une liste gratuite qui accepte les articles de n'importe quelle boutique, où les proches réservent sans compte et où la surprise est gardée jusqu'au lendemain de l'événement. Amazon garde une réduction de 15 % (plafonnée à 300 €) pour les titulaires d'une liste de naissance, que Listou ne propose pas.",
    ],
    [
      "Peut-on ajouter des articles d'autres boutiques que Amazon ?",
      "Sur Listou, oui : on colle le lien d'un produit de n'importe quelle boutique et le nom, la photo et le prix sont récupérés. Sur Amazon, l'aide décrit l'ajout d'articles par une recherche sur Amazon, vendeurs tiers de la Marketplace compris.",
    ],
    [
      "Sur la liste de naissance Amazon, voit-on les cadeaux achetés ?",
      "Le paramètre « Ne pas me gâcher la surprise » est activé par défaut : les cadeaux restent secrets pendant plusieurs semaines. L'aide précise que le filtre des articles achetés et les avertissements s'affichent seulement quand on est connecté, et que sans identification la liste peut révéler une surprise. Sur Listou, le mode surprise cache les réservations jusqu'au lendemain de l'événement.",
    ],
    [
      "Un article acheté en dehors de la liste Amazon est-il marqué comme acheté ?",
      "Non. D'après l'aide Amazon, seuls les articles ajoutés au panier depuis la liste sont marqués comme achetés ; un article commandé depuis sa page produit ne l'est pas. Sur Listou, l'invité réserve l'article avant de l'acheter où il veut, et la liste le montre comme pris.",
    ],
    [
      "Quelle est la réduction de la liste de naissance Amazon ?",
      "Amazon offre 15 % de réduction aux titulaires d'une liste de naissance, sur les articles éligibles repris dans la page de réduction, pour des commandes d'une valeur totale maximale de 2 000 €, soit 300 € de réduction au plus. Seul le titulaire principal en bénéficie. Listou est gratuit mais ne propose pas de réduction, puisqu'il ne vend rien.",
    ],
    [
      "Peut-on gérer une liste de naissance à deux ?",
      "Sur Amazon, la liste a un cotitulaire et peut être partagée en « Lecture et modification » avec des invités. Sur Listou, le co-parent devient co-propriétaire, avec les mêmes droits, et un invité « Peut modifier » aide à remplir la liste sans toucher au partage ni à la surprise.",
    ],
    [
      "Faut-il un compte pour réserver un cadeau sur Listou ?",
      "Non. Vos proches ouvrent le lien et cliquent sur « Je l'offre » ; le prénom et l'email sont facultatifs. Vous, en tant que créateur, vous vous connectez avec un lien envoyé par email ou avec Google.",
    ],
    [
      "Peut-on personnaliser le design de sa liste ?",
      "Sur Listou, oui, entièrement : 6 modèles de page, 13 palettes et une couleur d'accent, 4 polices de titre, 162 stickers en 18 thèmes, des fonds à motifs et une photo de couverture. Les pages d'aide Amazon sur la liste de naissance ne décrivent pas d'option de design.",
    ],
    [
      "Listou est-il vraiment gratuit ?",
      "Oui. Listou se rémunère par une commission que certaines boutiques versent quand un proche achète via la liste, sans changer le prix.",
    ],
  ],
  sources: [
    {
      label: "Amazon : Listes de naissance (sommaire de l'aide)",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GZAKR9PCZFCXUSVR",
    },
    {
      label: "Amazon : réduction sur la liste de naissance",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GV4FFW94JAGFL4RH",
    },
    {
      label: "Amazon : créer votre liste de naissance",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GXU26CGG4DXNYTU6",
    },
    {
      label: "Amazon : modifier les articles de votre liste de naissance",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GLLZRHKT5FKQBPBK",
    },
    {
      label: "Amazon : partager votre liste de naissance",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GW4LB8JTVK3HN775",
    },
    {
      label: "Amazon : paramètres de confidentialité de la liste de naissance",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GNMN896UDCJERA5M",
    },
    {
      label: "Amazon : rechercher une liste de naissance",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GXFECESNMMKTW3CX",
    },
    {
      label: "Amazon : acheter un article d'une liste de naissance",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GX7RDQYYVS85LM6X",
    },
    {
      label: "Amazon : activer les expéditions par des tiers",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=TKBD0x2Fr643Mut60n",
    },
    {
      label: "Amazon : partager votre liste (lecture seule ou modification)",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GPWMKQXXSBT8NAHN",
    },
    {
      label: "Amazon : configurer les paramètres de surprise",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=G9PJNRV76JRAXDJZ",
    },
    {
      label: "Amazon : acheter un article à partir d'une liste cadeaux",
      url: "https://www.amazon.fr/gp/help/customer/display.html?nodeId=GFDBP9AP8VYVQRHJ",
    },
  ],
};
